import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
} from '@angular/core';
import { BehaviorSubject, combineLatest, fromEvent, Subject } from 'rxjs';
import { debounceTime, startWith, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'atx-horizontal-scroll-arrows',
  templateUrl: './horizontal-scroll-arrows.component.html',
  styleUrls: ['./horizontal-scroll-arrows.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HorizontalScrollArrowsComponent
  implements AfterViewInit, OnDestroy
{
  public scrollState = {
    hasScrollBar: false,
    scrolledToLeft: true,
    scrolledToRight: false,
    showHasMore: true,
  };

  @Input()
  public hostElement: HTMLElement;

  public hasOverflow$ = new BehaviorSubject(false);
  private destroy$ = new Subject<void>();

  constructor(private cdRef: ChangeDetectorRef) {}

  public ngAfterViewInit() {
    if (this.hostElement) {
      const resize$ = new Subject<void>();
      this.checkOverflow();
      this.updateScrollState();
      new ResizeObserver(() => resize$.next()).observe(this.hostElement);

      combineLatest([
        resize$,
        fromEvent(this.hostElement, 'scroll').pipe(startWith('')),
      ])
        .pipe(debounceTime(50), takeUntil(this.destroy$))
        .subscribe(() => {
          this.updateScrollState();
        });
    }
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkOverflow() {
    const element = this.hostElement;
    this.hasOverflow$.next(element.offsetWidth < element.scrollWidth);
  }

  public scrollRight() {
    this.hostElement.scrollTo({
      left: this.hostElement.scrollLeft + 250,
      behavior: 'smooth',
    });
  }

  public scrollLeft() {
    this.hostElement.scrollTo({
      left: this.hostElement.scrollLeft - 250,
      behavior: 'smooth',
    });
  }

  public updateScrollState() {
    const element = this.hostElement;

    if (element) {
      const { scrollLeft, scrollWidth, offsetWidth } = element;

      if (
        (this.scrollState.showHasMore && scrollLeft >= 240) ||
        scrollWidth - offsetWidth < 250
      ) {
        this.scrollState.showHasMore = false;
      }

      this.scrollState = {
        ...this.scrollState,
        hasScrollBar: scrollWidth > offsetWidth,
        scrolledToLeft: scrollLeft === 0,
        scrolledToRight:
          scrollWidth === scrollLeft + offsetWidth ||
          scrollLeft + offsetWidth > scrollWidth,
      };
    } else {
      this.scrollState = {
        ...this.scrollState,
        hasScrollBar: false,
        scrolledToLeft: true,
        scrolledToRight: false,
      };
    }

    this.cdRef.markForCheck();
  }
}
